import React from 'react';
import cn from 'classnames';
import { Section } from '@repo/components/containers';
import { useDispatch, useSelector } from 'react-redux';
import { ID } from '@repo/utils/types';
import Contact from '@repo/utils/models/Contact';
import Note from '@repo/utils/models/Note';
import { TimelineNote, NewTimelineNote } from '@repo/components/notes';
import { TimelineItem } from '@repo/utils/models/Timeline';
import { selectContactTimeline } from '@repo/redux/reducers/data/timeline';
import { TimelineInteraction } from '@repo/components/timeline';
import { ChevronDownIcon, ChevronUpDownIcon, LoaderIcon } from '@repo/icons';

import { selectContactTimelineWindowSummary } from '@repo/redux/reducers/data/timeline';
import {} from '@repo/icons';
import { fetchContactTimelineWindow } from '@repo/redux/actions/timeline';
import { pluralize } from '@repo/utils/helpers/string';

import styles from './TimelineList.module.scss';

export const ExpandTimelineWindowButton = ({ contact }: { contact: Contact }) => {
  const dispatch = useDispatch();
  const timeline = useSelector(selectContactTimeline(contact.id));
  const windowSummary = useSelector(selectContactTimelineWindowSummary(contact.id));
  const { events, emails, calls, notes, total } = windowSummary || {};
  const shouldRender = windowSummary && total > 0;
  const loading = timeline?.isFetching || windowSummary?.isFetching;

  return shouldRender ? (
    <button
      className={styles.expandBtn}
      onClick={() => dispatch(fetchContactTimelineWindow({ contactID: contact.id }))}
      disabled={loading}
    >
      {loading ? (
        <LoaderIcon />
      ) : (
        <div className={styles.expandIcon}>
          <ChevronUpDownIcon />
        </div>
      )}
      <div className={styles.summary}>
        {(events || 0) > 0 && (
          <span>
            {events} {pluralize(events, 'Event')}
          </span>
        )}
        {(emails || 0) > 0 && (
          <span>
            {emails} {pluralize(events, 'Email')}
          </span>
        )}
        {(notes || 0) > 0 && (
          <span>
            {notes} {pluralize(events, 'Note')}
          </span>
        )}
        {(calls || 0) > 0 && (
          <span>
            {calls} {pluralize(calls, 'Call')}
          </span>
        )}
      </div>
    </button>
  ) : null;
};

export const TimelineItemsList = ({
  contact,
  timelineItems,
  showWindowButtonAfterID,
  currentlyEditingNote,
  clearNewNote,
  deleteNote,
  setCurrentlyEditing,
  clearCurrentlyEditing,
  filterApplied,
  loadMoreFilteredTimeline,
  loadingFilteredTimeline,
  noNewNoteTrigger,
  className,
}: {
  contact: Contact;
  timelineItems: TimelineItem[];
  showWindowButtonAfterID?: ID;
  currentlyEditingNote?: Note;
  clearNewNote?: (note: Note) => void;
  deleteNote?: (note: Note) => void;
  setCurrentlyEditing?: (note: Note, e: any) => void;
  clearCurrentlyEditing?: () => void;
  filterApplied: boolean;
  loadMoreFilteredTimeline?: () => void;
  loadingFilteredTimeline?: boolean;
  noNewNoteTrigger?: boolean;
  className?: string;
}) => {
  const emptyTimeline = timelineItems.length === 0;

  return (
    <Section style={{ marginTop: 32 }} className={cn(className)}>
      {!filterApplied && !noNewNoteTrigger && (
        <NewTimelineNote clearNewNote={clearNewNote} emptyTimeline={emptyTimeline} />
      )}
      {timelineItems.map((timelineItem, idx, timeline) => {
        const isLast = idx === timeline.length - 1;
        const includeWindowButton = !filterApplied && timelineItem.id === showWindowButtonAfterID;
        let renderedItem = null;

        if (timelineItem.type === 'note') {
          renderedItem = (
            <TimelineNote
              noteID={timelineItem.noteID}
              currentlyEditingNote={currentlyEditingNote}
              setCurrentlyEditing={setCurrentlyEditing}
              clearCurrentlyEditing={clearCurrentlyEditing}
              deleteNote={deleteNote}
              user={timelineItem.user}
              isLast={isLast}
            />
          );
        } else {
          renderedItem = (
            <TimelineInteraction contact={contact} timelineItem={timelineItem} isLast={isLast} />
          );
        }

        return (
          <React.Fragment key={`${timelineItem.stableID}_${String(includeWindowButton)}`}>
            {renderedItem}
            {includeWindowButton && <ExpandTimelineWindowButton contact={contact} />}
          </React.Fragment>
        );
      })}
      {loadMoreFilteredTimeline && (
        <button
          className={styles.expandBtn}
          onClick={loadMoreFilteredTimeline}
          disabled={loadingFilteredTimeline}
        >
          {loadingFilteredTimeline ? (
            <LoaderIcon />
          ) : (
            <div className={styles.expandIcon}>
              <ChevronDownIcon width={7} height={7} color="textSecondary" />
            </div>
          )}
          <div className={styles.summary}>Show more</div>
        </button>
      )}
    </Section>
  );
};
